import React from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { FavoritesSortType, IFavoritesFilter } from '../dto/IDiscoveryFilter';

interface IDiscoveryFavoritesUserListProps {
    children: React.ReactNode;
    filterParams: IFavoritesFilter;
    setFilterParams: React.Dispatch<React.SetStateAction<IFavoritesFilter>>;
    totalCount: number;
    isGlobal: boolean;
    isClient?: boolean;
    isBundle?: boolean;
    isLoading?: boolean;
}

interface SortableColumn {
    key: FavoritesSortType;
    label: string;
    translationKey: string;
}

const sortableColumns: SortableColumn[] = [
    // {
    //     key: FavoritesSortType.REACTIONS,
    //     label: 'reactions',
    //     translationKey: 'brand.campaign.discovery.reactions',
    // },
    {
        key: FavoritesSortType.PRIORITY,
        label: 'priority',
        translationKey: 'brand.campaign.discovery.priority',
    },
    {
        key: FavoritesSortType.FOLLOWERS,
        label: 'followers_subscribers',
        translationKey: 'brand.campaign.discovery.followers',
    },
    {
        key: FavoritesSortType.ENGAGEMENT_RATE,
        label: 'engagement_rate',
        translationKey: 'brand.campaign.discovery.er',
    },
    // {
    //     key: FavoritesSortType.ENGAGEMENTS,
    //     label: 'engagement_views',
    //     translationKey: 'brand.campaign.discovery.engagement_views',
    // },
];

const DiscoveryFavoritesUserList: React.FC<IDiscoveryFavoritesUserListProps> = ({
    children,
    filterParams,
    setFilterParams,
    totalCount,
    isClient,
    isGlobal,
    isBundle,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const canViewPurchaseOrder = useSelector((states: IAllStates) => states.auth.user.company?.canViewPurchaseOrder);
    const handleSort = (sortBy: FavoritesSortType) => {
        setFilterParams(prev => ({
            ...prev,
            sortType: prev.sortBy === sortBy && prev.sortType === 'DESC' ? 'ASC' : 'DESC',
            sortBy,
        }));
    };

    const renderSortableHeader = (col: SortableColumn) => {
        const isActive = filterParams.sortBy === col.key;
        const isDesc = filterParams.sortType === 'DESC';
        return (
            <th
                key={col.key}
                className={clsx('text-muted text-center cursor-pointer', 'sortable-header', isActive && 'text-primary')}
                onClick={() => handleSort(col.key)}
                role="button"
                tabIndex={0}
                aria-sort={isActive ? (isDesc ? 'descending' : 'ascending') : undefined}
                style={{ userSelect: 'none', minWidth: 84 }}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort(col.key);
                    }
                }}
            >
                <div className="d-flex align-items-center justify-content-center w-max">
                    {isDesc && isActive ? (
                        <FaSortAmountDown className={clsx('mr-1 font-12', isActive && 'text-primary')} />
                    ) : (
                        <FaSortAmountUp className={clsx('mr-1 font-12', isActive && 'text-primary')} />
                    )}
                    {t(col.translationKey)}
                </div>
            </th>
        );
    };

    return (
        <Table responsive className="table-vertical-center table-with-spacing mb-md-5">
            <thead className="py-2 border-bottom-table-header">
                <tr>
                    <th className="text-muted position-sticky left-0 bg-light-gray pr-0" style={{ zIndex: 1 }}>
                        <div className="border-right py-2">
                            {t('brand.campaign.discovery.influencers_saved', {
                                nbInfluencers: isLoading ? '–' : totalCount,
                            })}
                        </div>
                    </th>
                    <th className="text-muted text-center">{t('general.platform')}</th>
                    {/*{!isClient && (*/}
                    {/*      <th className="text-muted text-center">*/}
                    {/*          {t('brand.campaign.discovery.remove_from_favorites')}*/}
                    {/*      </th>*/}
                    {/*  )}*/}
                    <th className="text-muted text-center">{t('brand.campaign.discovery.email')}</th>
                    {sortableColumns.map(renderSortableHeader)}
                    <th className="text-muted text-center">{t('brand.campaign.discovery.female')}</th>
                    <th className="text-muted text-center">{t('brand.campaign.discovery.male')}</th>
                    <th className="text-muted">{t('brand.campaign.discovery.top_audience_country_1')}</th>
                    <th className="text-muted">{t('brand.campaign.discovery.top_audience_country_2')}</th>
                    <th className="text-muted">{t('brand.campaign.discovery.top_audience_country_3')}</th>
                    <th className="text-muted">{t('brand.campaign.discovery.collabs')}</th>
                    <th className="text-muted text-center">{t('brand.campaign.discovery.price')}</th>
                    {canViewPurchaseOrder && (
                        <th className="text-muted text-center">{t('brand.campaign.discovery.po')}</th>
                    )}
                    {!isGlobal && <th className="text-muted text-start">{t('brand.campaign.discovery.status')}</th>}
                    <th className="text-muted text-start">{t('brand.campaign.discovery.note')}</th>
                    {!isGlobal && (
                        <th className="text-muted text-start">{t('brand.campaign.discovery.client_note')}</th>
                    )}
                    <th className="text-end text-muted">{t('general.actions')}</th>
                    {(isBundle ? !isClient : true) && <th className="text-end text-muted"></th>}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </Table>
    );
};

export default DiscoveryFavoritesUserList;
